import { acceptHMRUpdate, defineStore } from "pinia";
import type Invitation from "../../../backend/app/models/invitation";

export const useInviteStore = defineStore("invite", () => {
  const auth = useAuthStore();
  const toast = useToast();
  const sentInvitations = ref<Partial<Invitation>[]>([]);
  const invitations = ref<Invitation[]>([]);

  const getSentInvitations = async () => {
    try {
      const resp = (await auth.api("get", "/invite/get")) as {
        invitations: Invitation[];
        message: string;
      };
      sentInvitations.value = resp.invitations;
    } catch (error: any) {
      console.error("Error fetching invitations:", error);
      toast.add({
        title: "Error",
        description: error.message || "Could not fetch sent invitations.",
        color: "error",
      });
    }
  };

  const getInvitations = async () => {
    try {
      const resp = (await auth.api("get", "/invite/user")) as {
        invitations: Invitation[];
        message: string;
      };
      invitations.value = resp.invitations;
    } catch (error: any) {
      const description = error.message || "Could not fetch invitations. Please try again.";
      toast.add({ title: "Error", description, color: "error" });
      return description;
    }
  };

  const acceptInvitation = async (invitationId: string, navigate: boolean = true) => {
    try {
      const resp = (await auth.api("post", "/invite/accept", {
        invitation_id: invitationId,
      })) as { invitation: Invitation; message: string };
      await getInvitations(); // Refresh invitations list
      toast.add({
        title: "Success",
        description: resp.message,
        color: "green",
      });
      if (navigate) {
        navigateTo("/user", { external: true });
      }
      return true;
    } catch (error: any) {
      console.error("Error accepting invitation:", error);
      const description = error.message || "Could not accept invitation. Please try again.";
      toast.add({ title: "Error", description, color: "error" });
      return false;
    }
  };

  const rejectInvitation = async (invitationId: string) => {
    try {
      const resp = (await auth.api("post", "/invite/reject", {
        invitation_id: invitationId,
      })) as { invitation: Invitation; message: string };
      await getInvitations(); // Refresh invitations list
      toast.add({
        title: "Success",
        description: resp.message,
        color: "green",
      });
      return true;
    } catch (error: any) {
      console.error("Error rejecting invitation:", error);
      const description = error.message || "Could not reject invitation. Please try again.";
      toast.add({ title: "Error", description, color: "error" });
      return false;
    }
  };

  const sendInvitation = async (email: string) => {
    try {
      const resp = (await auth.api("post", "/invite/send", {
        email: email,
      })) as { message: string };
      await getSentInvitations();
      return resp.message;
    } catch (error: any) {
      throw new Error(error.message || "Failed to send invitation. Please try again.", {
        cause: error,
      });
    }
  };

  const deleteInvitation = async (invitationId: string) => {
    try {
      const resp = (await auth.api("delete", "/invite/delete", {
        invitation_id: invitationId,
      })) as { message: string };

      await getSentInvitations(); // Refresh invitations list
      toast.add({
        title: "Success",
        description: resp.message,
        color: "green",
      });
      return resp.message;
    } catch (error: any) {
      console.error("Error deleting invitation:", error);
      const description = error.message || "Could not delete invitation. Please try again.";
      toast.add({ title: "Error", description, color: "error" });
      return false;
    }
  };
  return {
    invitations,
    getInvitations,
    acceptInvitation,
    rejectInvitation,
    sendInvitation,
    getSentInvitations,
    sentInvitations,
    deleteInvitation,
  };
});

// Make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInviteStore, import.meta.hot));
}
